/**
 * useInstallPrompt — sdílený stav instalovatelnosti PWA (jeden zdroj pravdy).
 *
 * Prohlížeč (Chromium: Chrome/Edge/…) vystřelí `beforeinstallprompt`, když appka
 * splňuje kritéria instalace (manifest + SW + ikony + HTTPS) a ještě není
 * nainstalovaná. Událost přijde JEN JEDNOU a odloženou instanci (`prompt()`) lze
 * použít JEN JEDNOU — proto ji držíme v module-level singletonu a sdílíme mezi
 * všemi konzumenty (globální banner `InstallPrompt` i sekce na `/about`). Kdyby si
 * ji každá komponenta odchytávala vlastním listenerem, přebíjely by se.
 *
 * iOS Safari `beforeinstallprompt` NEpodporuje (instalace přes „Sdílet → Přidat na
 * plochu") → `canInstall` tam zůstane `false`; UI musí nabídnout ruční návod samo.
 */
import { useCallback, useEffect, useReducer } from "react";

/** `beforeinstallprompt` není ve standardních TS lib typech — minimální tvar. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

// Listenery registrujeme hned při importu modulu (ne až v effectu), aby nám
// neutekla událost, která může přijít dřív, než se komponenta namountuje.
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault(); // potlač defaultní mini-infobar — řídíme si UI sami
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    notify();
  });
}

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

export interface UseInstallPrompt {
  /** True = prohlížeč nabídl nativní instalaci a appka ještě není nainstalovaná. */
  canInstall: boolean;
  /** True = appka byla během této session nainstalována (`appinstalled`). */
  installed: boolean;
  /** Spustí systémový instalační dialog. Prompt lze použít jen jednou. */
  promptInstall: () => Promise<InstallOutcome>;
}

export function useInstallPrompt(): UseInstallPrompt {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => {
      listeners.delete(force);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    if (!deferred) return "unavailable";
    const evt = deferred;
    deferred = null; // jednorázové — hned „spotřebuj", ať ho nikdo nepoužije podruhé
    notify();
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      return choice.outcome;
    } catch {
      // uživatel dialog zavřel / chyba
      return "dismissed";
    }
  }, []);

  return { canInstall: deferred !== null && !installed, installed, promptInstall };
}
